"use client";

import { useState } from "react";
import { Play } from "lucide-react";
import type { BlogVideo } from "@/data/blog/types";

interface VideoFacadeProps {
  video: BlogVideo;
}

/**
 * Click-to-load YouTube facade (PRD 03 Epic B / PRD 07 performance): renders
 * only a thumbnail + play button until the user clicks, so no third-party
 * iframe or JS loads on page view (protects LCP/CLS). The iframe uses the
 * privacy-enhanced youtube-nocookie.com domain. The VideoObject JSON-LD is
 * emitted server-side by the blog page, not here.
 */
export default function VideoFacade({ video }: VideoFacadeProps) {
  const [loaded, setLoaded] = useState(false);
  const thumb = `https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`;

  return (
    <figure className="blog-video">
      <div className="blog-video-frame">
        {loaded ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <button
            type="button"
            className="blog-video-poster"
            onClick={() => setLoaded(true)}
            aria-label={`Play video: ${video.title}`}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={thumb} alt={video.title} width={480} height={360} loading="lazy" />
            <span className="blog-video-play"><Play size={28} fill="currentColor" /></span>
          </button>
        )}
      </div>
      <figcaption className="blog-video-caption">
        {video.title} — via {video.channel}
      </figcaption>
      <style jsx>{`
        .blog-video {
          margin: var(--space-5) 0;
        }
        .blog-video-frame {
          position: relative;
          width: 100%;
          aspect-ratio: 16 / 9;
          border-radius: var(--radius-md);
          overflow: hidden;
          border: 1px solid var(--border-color);
          background: var(--bg-elevated);
        }
        .blog-video-frame :global(iframe) {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: 0;
        }
        .blog-video-poster {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          padding: 0;
          border: 0;
          cursor: pointer;
          background: var(--bg-elevated);
        }
        .blog-video-poster :global(img) {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .blog-video-play {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: var(--primary);
          color: #fff;
          box-shadow: var(--shadow-lg);
          transition: transform var(--transition-fast);
        }
        .blog-video-poster:hover .blog-video-play {
          transform: translate(-50%, -50%) scale(1.08);
        }
        .blog-video-caption {
          margin-top: var(--space-3);
          font-size: var(--text-sm);
          color: var(--text-muted);
          text-align: center;
          font-style: italic;
        }
      `}</style>
    </figure>
  );
}
